import type { Division, Pack } from '../types/deck-builder';
import type { PatchDiffResult } from './patch-diff';
import type { UnitWithWeapons } from './unit-weapons-joined';

/**
 * Availability change for a unit in a division
 */
export interface AvailabilityChange {
  unitDescriptor: string;
  pack: Pack;
  oldPack: Pack | null;
  changes: {
    numberOfCards?: { old: number; new: number };
    numberOfUnitsInPack?: { old: number; new: number };
    numberOfUnitInPackXPMultiplier?: { old: number[]; new: number[] };
    availableWithoutTransport?: { old: boolean; new: boolean };
    availableTransportList?: { old: string[] | undefined; new: string[] | undefined };
  };
}

/**
 * Division change information
 */
export interface DivisionChange {
  division: Division;
  oldDivision: Division | null;
  addedUnits: string[]; // Unit descriptors
  removedUnits: string[]; // Unit descriptors
  availabilityChanges: AvailabilityChange[];
  unitChanges: Array<{
    unit: UnitWithWeapons;
    oldUnit: UnitWithWeapons;
    diff: any;
  }>;
}

/**
 * Division diff result grouped by division
 */
export interface DivisionDiffResult {
  [divisionDescriptor: string]: DivisionChange;
}

/**
 * Compare divisions between two versions and extract changes
 * @param oldDivisions Array of divisions from old version
 * @param newDivisions Array of divisions from new version
 * @param patchDiffResult Patch diff result from unit comparison
 * @returns Division diff result grouped by division descriptor
 */
export function calculateDivisionDiff(
  oldDivisions: Division[],
  newDivisions: Division[],
  patchDiffResult: PatchDiffResult
): DivisionDiffResult {
  const result: DivisionDiffResult = {};

  // Create maps for quick lookup
  const oldDivisionsMap = new Map<string, Division>();
  const newDivisionsMap = new Map<string, Division>();
  const oldPacksMap = new Map<string, Map<string, Pack>>(); // division -> unitDescriptor -> pack
  const newPacksMap = new Map<string, Map<string, Pack>>(); // division -> unitDescriptor -> pack

  oldDivisions.forEach((division) => {
    oldDivisionsMap.set(division.descriptor, division);
    const packsMap = new Map<string, Pack>();
    division.packs.forEach((pack) => {
      packsMap.set(pack.unitDescriptor, pack);
    });
    oldPacksMap.set(division.descriptor, packsMap);
  });

  newDivisions.forEach((division) => {
    newDivisionsMap.set(division.descriptor, division);
    const packsMap = new Map<string, Pack>();
    division.packs.forEach((pack) => {
      packsMap.set(pack.unitDescriptor, pack);
    });
    newPacksMap.set(division.descriptor, packsMap);
  });

  // Process all divisions (both old and new)
  const allDivisionDescriptors = new Set([
    ...oldDivisionsMap.keys(),
    ...newDivisionsMap.keys(),
  ]);

  allDivisionDescriptors.forEach((divisionDescriptor) => {
    const oldDivision = oldDivisionsMap.get(divisionDescriptor) || null;
    const newDivision = newDivisionsMap.get(divisionDescriptor) || null;
    const oldPacks = oldPacksMap.get(divisionDescriptor) || new Map();
    const newPacks = newPacksMap.get(divisionDescriptor) || new Map();

    if (!newDivision) {
      // Division was removed - skip for now (could add if needed)
      return;
    }

    const addedUnits: string[] = [];
    const removedUnits: string[] = [];
    const availabilityChanges: AvailabilityChange[] = [];

    // Find units added to division
    newPacks.forEach((pack, unitDescriptor) => {
      if (!oldPacks.has(unitDescriptor)) {
        addedUnits.push(unitDescriptor);
      }
    });

    // Find units removed from division
    oldPacks.forEach((pack, unitDescriptor) => {
      if (!newPacks.has(unitDescriptor)) {
        removedUnits.push(unitDescriptor);
      }
    });

    // Find availability changes for units in both versions
    newPacks.forEach((newPack, unitDescriptor) => {
      const oldPack = oldPacks.get(unitDescriptor);
      
      if (oldPack) {
        const changes: AvailabilityChange['changes'] = {};

        // Check numberOfCards
        if (oldPack.numberOfCards !== newPack.numberOfCards) {
          changes.numberOfCards = {
            old: oldPack.numberOfCards,
            new: newPack.numberOfCards,
          };
        }

        // Check numberOfUnitsInPack
        if (oldPack.numberOfUnitsInPack !== newPack.numberOfUnitsInPack) {
          changes.numberOfUnitsInPack = {
            old: oldPack.numberOfUnitsInPack,
            new: newPack.numberOfUnitsInPack,
          };
        }

        // Check numberOfUnitInPackXPMultiplier
        const oldXpMultiplier = JSON.stringify(oldPack.numberOfUnitInPackXPMultiplier || []);
        const newXpMultiplier = JSON.stringify(newPack.numberOfUnitInPackXPMultiplier || []);
        if (oldXpMultiplier !== newXpMultiplier) {
          changes.numberOfUnitInPackXPMultiplier = {
            old: oldPack.numberOfUnitInPackXPMultiplier || [],
            new: newPack.numberOfUnitInPackXPMultiplier || [],
          };
        }

        // Check availableWithoutTransport
        if (oldPack.availableWithoutTransport !== newPack.availableWithoutTransport) {
          changes.availableWithoutTransport = {
            old: oldPack.availableWithoutTransport,
            new: newPack.availableWithoutTransport,
          };
        }

        // Check availableTransportList
        const oldTransportList = JSON.stringify(oldPack.availableTransportList || []);
        const newTransportList = JSON.stringify(newPack.availableTransportList || []);
        if (oldTransportList !== newTransportList) {
          changes.availableTransportList = {
            old: oldPack.availableTransportList,
            new: newPack.availableTransportList,
          };
        }

        // If there are any changes, add to availabilityChanges
        if (Object.keys(changes).length > 0) {
          availabilityChanges.push({
            unitDescriptor,
            pack: newPack,
            oldPack,
            changes,
          });
        }
      }
    });

    // Find unit stat changes for units in this division
    const unitChanges: DivisionChange['unitChanges'] = [];
    patchDiffResult.changed.forEach((change) => {
      // Check if this unit is in the new division
      if (newPacks.has(change.unit.descriptorName)) {
        unitChanges.push(change);
      }
    });

    // Only add division to result if there are any changes
    if (
      addedUnits.length > 0 ||
      removedUnits.length > 0 ||
      availabilityChanges.length > 0 ||
      unitChanges.length > 0
    ) {
      result[divisionDescriptor] = {
        division: newDivision,
        oldDivision,
        addedUnits,
        removedUnits,
        availabilityChanges,
        unitChanges,
      };
    }
  });

  return result;
}

